import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Stops a render fault in one route from blanking the whole document.
 *
 * The site holds no user input worth preserving, so recovery is a reload rather
 * than a retry of the failed subtree.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled render error', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--wft-bg-0)',
        }}
      >
        <div style={{ maxWidth: '520px' }}>
          <p
            style={{
              fontFamily: 'var(--wft-mono)',
              fontSize: '10.5px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--wft-down)',
              margin: '0 0 10px',
            }}
          >
            Render fault
          </p>
          <h1
            style={{
              fontFamily: 'var(--wft-display)',
              fontSize: '24px',
              lineHeight: 1.25,
              color: 'var(--wft-ink-0)',
              margin: '0 0 12px',
            }}
          >
            This surface failed to draw.
          </h1>
          <p
            style={{
              fontFamily: 'var(--wft-sans)',
              fontSize: '14px',
              lineHeight: 1.6,
              color: 'var(--wft-ink-1)',
              margin: '0 0 20px',
            }}
          >
            Rather than show a partially drawn panel, the page stopped. Reloading is the supported
            recovery.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              fontFamily: 'var(--wft-mono)',
              fontSize: '11px',
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              padding: '10px 18px',
              border: '1px solid var(--wft-accent-bd)',
              borderRadius: '3px',
              background: 'var(--wft-accent-bg)',
              color: 'var(--wft-accent)',
            }}
          >
            Reload
          </button>
        </div>
      </main>
    );
  }
}
